'use client';

import { signIn } from 'next-auth/react';

import {
  getConfiguredYandexHelperUrl,
  getYandexLocalConfigError,
  isLoopbackOrigin,
  isYandexLocalHelperConfigured,
  requestLocalYandexAccessToken,
  resolveOrigin,
  YANDEX_LOCAL_PROVIDER_ID,
} from '@/lib/yandexLocalAuth';

type PublicYandexAuthInput = {
  yandexAuthEnabled: boolean;
  yandexClientId: string | null;
};

type PublicYandexAuthState = {
  yandexAuthEntrypointEnabled: boolean;
  errorMessage: string;
};

export function resolvePublicYandexAuthState({
  yandexAuthEnabled,
  yandexClientId,
}: PublicYandexAuthInput): PublicYandexAuthState {
  const configuredAppUrl = process.env.NEXT_PUBLIC_APP_URL ?? null;
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  const helperUrl = getConfiguredYandexHelperUrl();
  const effectiveOrigin = resolveOrigin(currentOrigin) ?? resolveOrigin(configuredAppUrl);
  const isLocalOrigin = isLoopbackOrigin(effectiveOrigin);
  const localYandexReady = Boolean(yandexClientId?.trim()) && isYandexLocalHelperConfigured(helperUrl);
  const yandexAuthEntrypointEnabled = yandexAuthEnabled && (!isLocalOrigin || localYandexReady);
  const errorMessage = isLocalOrigin
    ? getYandexLocalConfigError(helperUrl)
    : 'Вход через Yandex сейчас недоступен.';

  return {
    yandexAuthEntrypointEnabled,
    errorMessage,
  };
}

export async function signInWithPublicYandex(
  input: PublicYandexAuthInput & { callbackUrl: string },
): Promise<void> {
  const { yandexAuthEnabled, yandexClientId, callbackUrl } = input;
  const helperUrl = getConfiguredYandexHelperUrl();
  const configuredAppUrl = process.env.NEXT_PUBLIC_APP_URL ?? null;
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  const effectiveOrigin = resolveOrigin(currentOrigin) ?? resolveOrigin(configuredAppUrl);
  const { yandexAuthEntrypointEnabled, errorMessage } = resolvePublicYandexAuthState({
    yandexAuthEnabled,
    yandexClientId,
  });

  if (!yandexAuthEnabled || !yandexAuthEntrypointEnabled) {
    throw new Error(errorMessage);
  }

  if (isLoopbackOrigin(effectiveOrigin)) {
    if (!effectiveOrigin || !yandexClientId?.trim() || !helperUrl) {
      throw new Error(errorMessage);
    }

    const { accessToken } = await requestLocalYandexAccessToken({
      clientId: yandexClientId,
      helperUrl,
      origin: effectiveOrigin,
      intent: 'login',
      callbackUrl,
    });

    const result = await signIn(YANDEX_LOCAL_PROVIDER_ID, {
      redirect: false,
      accessToken,
      intent: 'login',
      callbackUrl,
    });

    if (result?.error) {
      throw new Error('Не удалось завершить вход через Yandex.');
    }

    window.location.assign(result?.url ?? callbackUrl);
    return;
  }

  await signIn('yandex', { callbackUrl });
}
